import * as Api from '@ViewModels';
import { useCallback, useState } from 'react';

const LOCATION_PROPERTIES = [
	Api.ContactFilterCriteriaProperty.City,
	Api.ContactFilterCriteriaProperty.StateProvince,
	Api.ContactFilterCriteriaProperty.PostalCode,
] as const;

type LocationProperty = (typeof LOCATION_PROPERTIES)[number];

const findLocationValue = (criteria: Api.IContactFilterCriteria[], property: LocationProperty): string => {
	const criterion = criteria.find(c => c.property === property);
	return criterion?.value ?? '';
};

export const useLocationFilters = (initialFilterCriteria: Api.IContactFilterCriteria[] = []) => {
	const [city, setCity] = useState(() =>
		findLocationValue(initialFilterCriteria, Api.ContactFilterCriteriaProperty.City)
	);
	const [stateProvince, setStateProvince] = useState(() =>
		findLocationValue(initialFilterCriteria, Api.ContactFilterCriteriaProperty.StateProvince)
	);
	const [postalCode, setPostalCode] = useState(() =>
		findLocationValue(initialFilterCriteria, Api.ContactFilterCriteriaProperty.PostalCode)
	);

	const getLocationCriteria = useCallback((): Api.IContactFilterCriteria[] => {
		const criteria: Api.IContactFilterCriteria[] = [];

		if (city.trim()) {
			criteria.push({
				property: Api.ContactFilterCriteriaProperty.City,
				value: city.trim(),
			});
		}

		if (stateProvince.trim()) {
			criteria.push({
				property: Api.ContactFilterCriteriaProperty.StateProvince,
				value: stateProvince.trim(),
			});
		}

		if (postalCode.trim()) {
			criteria.push({
				property: Api.ContactFilterCriteriaProperty.PostalCode,
				value: postalCode.trim(),
			});
		}

		return criteria;
	}, [city, stateProvince, postalCode]);

	const refreshLocationFilters = useCallback((criteria: Api.IContactFilterCriteria[]) => {
		setCity(findLocationValue(criteria, Api.ContactFilterCriteriaProperty.City));
		setStateProvince(findLocationValue(criteria, Api.ContactFilterCriteriaProperty.StateProvince));
		setPostalCode(findLocationValue(criteria, Api.ContactFilterCriteriaProperty.PostalCode));
	}, []);

	const clearLocationFilters = useCallback(() => {
		setCity('');
		setStateProvince('');
		setPostalCode('');
	}, []);

	const hasLocationFilters = city.trim() !== '' || stateProvince.trim() !== '' || postalCode.trim() !== '';

	const isLocationCriteria = useCallback((criterion: Api.IContactFilterCriteria): boolean => {
		return LOCATION_PROPERTIES.includes(criterion.property as LocationProperty);
	}, []);

	return {
		city,
		setCity,
		stateProvince,
		setStateProvince,
		postalCode,
		setPostalCode,
		getLocationCriteria,
		refreshLocationFilters,
		clearLocationFilters,
		hasLocationFilters,
		isLocationCriteria,
	};
};
